<template>
	<div id="app" class="h-full" :class="vueAppClasses">
		<component :is="$store.getters.getLayout" @setAppClasses="setAppClasses"
				   :key="$store.getters.getLayout"></component>
		<transition name="fade-bottom">
			<Chat v-if="$store.state.chat.display"></Chat>
		</transition>
	</div>
</template>

<script>
import themeConfig from '@/../themeConfig.js';
import Client from '@/layouts/Client';
import Author from '@/layouts/Author';
import Manager from '@/layouts/Manager';
import Accountant from '@/layouts/Accountant';
import Admin from '@/layouts/Admin';
import Chat from '@/components/Chat/Index.vue';
import notifier from "@/mixins/notifier";
import { mapGetters } from "vuex"

export default {
	mixins: [notifier],
	data() {
		return {
			vueAppClasses: []
		}
	},
	components: {
		Client,
		Author,
		Accountant,
		Admin,
		Chat,
		Manager
	},
	computed: {
		...mapGetters([
			"websiteTitle",
			"isStaffUrl",
			"metaDescription",
			"websiteLogo",
			"getIsIntercomAvailable",
			"getUser"
		])
	},
	watch: {
		'$vs.rtl'(val) {
			document.documentElement.setAttribute('dir', val ? 'rtl' : 'ltr')
		},
		websiteTitle() {
			this.updatePageTitle()
		},
		metaDescription() {
			document
				.querySelector('meta[name="description"]')
				.setAttribute("content", this.metaDescription);
		},
		websiteLogo() {
			document
				.querySelector('link[rel="icon"]')
				.setAttribute("href", this.websiteLogo);
		},
		getIsIntercomAvailable(newValue) {
			this.initIntercom(newValue);
		}
	},
	async mounted() {
		if(this.$store.getters.isClientUrl) {
			await this.updateWebsiteInfo();
			this.updateColorsData();
		} else if(this.isStaffUrl) {
			this.setDefaultWebsite();
		}

		this.toggleClassInBody(themeConfig.theme)
		this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth)

		const vh = window.innerHeight * 0.01
		// Then we set the value in the --vh custom property to the root of the document
		document.documentElement.style.setProperty('--vh', `${vh}px`);
		this.initIntercom(this.getIsIntercomAvailable);


	},
	async created() {
		const dir = this.$vs.rtl ? 'rtl' : 'ltr'
		document.documentElement.setAttribute('dir', dir)

		window.addEventListener('resize', this.handleWindowResize)
		window.addEventListener('scroll', this.handleScroll)
	},
	destroyed() {
		window.removeEventListener('resize', this.handleWindowResize)
		window.removeEventListener('scroll', this.handleScroll)
	},
	methods: {
		updatePageTitle() {
			document.title = this.$store.getters.websiteTitle;
		},
		setDefaultWebsite() {
			this.$store.commit('setDefaultWebsite');
		},
		toggleClassInBody(className) {
			if (className === 'dark') {
				if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark')
				document.body.classList.add('theme-dark')
			} else if (className === 'semi-dark') {
				if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark')
				document.body.classList.add('theme-semi-dark')
			} else {
				if (document.body.className.match('theme-dark')) document.body.classList.remove('theme-dark')
				if (document.body.className.match('theme-semi-dark')) document.body.classList.remove('theme-semi-dark')
			}
		},
		setAppClasses(classesStr) {
			this.vueAppClasses.push(classesStr)
		},
		handleWindowResize() {
			this.$store.commit('UPDATE_WINDOW_WIDTH', window.innerWidth)

			// Set --vh property
			document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
		},
		handleScroll() {
			this.$store.commit('UPDATE_WINDOW_SCROLL_Y', window.scrollY)
		},
		getColorFromHEX(num) {
			return num.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
				, (m, r, g, b) => '#' + r + r + g + g + b + b)
				.substring(1).match(/.{2}/g)
				.map(x => parseInt(x, 16))
				.join(", ");
		},
		getColorFromRGB(num) {
			const startStr = "rgb(";
			const endStr = ")";
			const start = num.indexOf(startStr) + + startStr.length;
			const end = num.lastIndexOf(endStr);

			return num.substr(start, end - start);
		},
		changeColors(colors) {
			if (colors === undefined) return;

			const main = colors.theme_color_main;
			const secondary = colors.theme_color_secondary;
			let convertedMain = [];
			let convertedSecondary = [];

			if(main.indexOf("#") === 0) {
				convertedMain = this.getColorFromHEX(main);
			} else {
				convertedMain = this.getColorFromRGB(main);
			}

			if(secondary.indexOf("#") === 0) {
				convertedSecondary = this.getColorFromHEX(secondary);
			} else {
				convertedSecondary = this.getColorFromRGB(secondary);
			}

			this.$vs.theme({
				primary: convertedSecondary,
				main: convertedMain
			});
		},
		updateColorsData() {
			this.changeColors(this.$store.state.website.colors);
		},
		async updateWebsiteInfo () {
			try {
				await this.$store.dispatch("updateWebsiteInfo");

			} catch(e) {
				this.notifyError(e.message);
			}
		},
		initIntercom(isAvailable) {
			if(isAvailable) {
				const {first_name, email, id } = this.getUser;
				window.intercomSettings = {
					api_base: "https://api-iam.intercom.io",
					app_id: "tqop6ist",
					Website: "CRM",
				};
				Intercom('update', {
					'hide_default_launcher': false,
					name: first_name, // Full name
					email: email, // Email address
					user_id: id, // Author address

				})
			}
			else {
				Intercom('update', {
					'hide_default_launcher': true,
					name: '', // Full name
					email: '', // Email address
					user_id: '', // Author id

				})
			}
		}
	}
}

</script>
<style lang="scss">
@import "../src/assets/scss/vuexy/functions.scss";

#app {
	h1 {
		font-size: rem(18px);
		color: #626262;
		font-weight: 700;

	}

	.d-flex {
		display: flex;
	}

	.column {
		flex-direction: column;
	}

	.flex-end {
		justify-content: flex-end;
	}
}
</style>
